import React from 'react';
import {
  RichTextDescription,
  RichTextElement
} from 'coop-adventure-engine';
import { plainTextToRichText, isRichTextDescription } from '../../utils/richTextRenderer';
import { generateImageUrl } from '../../utils/imageUtils';
import IconImage from './IconImage.tsx';
import InlineOption from '../ui/InlineOption';
import InventoryList from '../ui/InventoryList';
import SelectableInventoryList from '../ui/SelectableInventoryList';
import FriendRequestList from '../ui/FriendRequestList';
import FriendsList from '../ui/FriendsList';
import AdventureInviteList from '../ui/AdventureInviteList';
import AdventureInviteStatusList from '../ui/AdventureInviteStatusList';
import AdventureList from './AdventureList';
import AdventureTitleImageFromId from './AdventureTitleImageFromId';
import ActiveAdventureList from './ActiveAdventureList';
import ActiveAdventureTitleImage from './ActiveAdventureTitleImage';
import PortraitImage from './PortraitImage';
import UserInputField from './UserInputField';

const PORTRAIT_VARIANTS = ['thumbnail', 'card', 'large'] as const;
const ICON_VARIANTS = ['small', 'large'] as const;
const DEFAULT_PORTRAIT_VARIANT = 'card';

const isPortraitVariant = (value: string): value is typeof PORTRAIT_VARIANTS[number] =>
  PORTRAIT_VARIANTS.includes(value as typeof PORTRAIT_VARIANTS[number]);

const isIconVariant = (value: string): value is typeof ICON_VARIANTS[number] =>
  ICON_VARIANTS.includes(value as typeof ICON_VARIANTS[number]);

export interface RichTextRendererProps {
  description: string | RichTextDescription;
  onInlineOptionClick?: (optionId: string) => void;
  onInventoryItemSelect?: (itemTag: string, targetScene: string) => void;
  onFriendRequestSelect?: (requestId: string, targetScene: string) => void;
  onFriendSelect?: (friendId: string, targetScene?: string) => void;
  onAdventureInviteClick?: (inviteId: string) => void;
  onAdventureInviteStatusSelect?: (inviteId: string, targetScene: string) => void;
  className?: string;
  style?: React.CSSProperties;
  customColor?: string; // Optional custom button color for inline options
  disableInlineOptions?: boolean; // When true (e.g. action pending), inline options are disabled
}

/**
 * Renders a RichTextDescription as React components
 * This is needed for inline options which require React components
 */
export const RichTextRenderer: React.FC<RichTextRendererProps> = ({
  description,
  onInlineOptionClick,
  onInventoryItemSelect,
  onFriendRequestSelect,
  onFriendSelect,
  onAdventureInviteClick,
  onAdventureInviteStatusSelect,
  className = '',
  style,
  customColor,
  disableInlineOptions = false,
}) => {
  const renderElement = (element: RichTextElement, index: number): React.ReactNode => {
    if (!element || typeof element.type !== 'string') {
      return null;
    }

    const key = `element-${index}`;

    switch (element.type) {
      case 'paragraph':
        const paragraphContent = Array.isArray(element.content) 
          ? element.content.map((child, childIndex) => renderElement(child, childIndex))
          : <SafeText text={element.content as string} />;
        return (
          <p key={key} className="rich-text-paragraph">
            {paragraphContent}
          </p>
        );

      case 'heading':
        const headingContent = Array.isArray(element.content) 
          ? element.content.map((child, childIndex) => renderElement(child, childIndex))
          : <SafeText text={element.content as string} />;
        const level = element.level || 1;
        const HeadingTag = `h${Math.min(Math.max(level, 1), 3)}` as keyof React.JSX.IntrinsicElements;
        return (
          <HeadingTag key={key} className={`rich-text-heading rich-text-heading-${level}`}>
            {headingContent}
          </HeadingTag>
        );

      case 'list':
        const listItems = Array.isArray(element.content) 
          ? element.content.map((item, itemIndex) => {
              if (!item) {
                return null;
              }
              // Handle both string content and array of rich text elements
              const itemContent = Array.isArray(item.content) 
                ? item.content.map((child, childIndex) => renderElement(child, childIndex))
                : <SafeText text={item.content as string} />;
              return (
                <li key={`item-${itemIndex}`} className="rich-text-list-item">
                  {itemContent}
                </li>
              );
            })
          : [];
        
        const ListTag = element.listType === 'numbered' ? 'ol' : 'ul';
        const listClass = `rich-text-list rich-text-list-${element.listType}`;
        return (
          <ListTag key={key} className={listClass}>
            {listItems}
          </ListTag>
        );

      case 'bold':
        const boldContent = Array.isArray(element.content) 
          ? element.content.map((child, childIndex) => renderElement(child, childIndex))
          : <SafeText text={element.content as string} />;
        return (
          <strong key={key} className="rich-text-bold">
            {boldContent}
          </strong>
        );

      case 'italic':
        const italicContent = Array.isArray(element.content) 
          ? element.content.map((child, childIndex) => renderElement(child, childIndex))
          : <SafeText text={element.content as string} />;
        return (
          <em key={key} className="rich-text-italic">
            {italicContent}
          </em>
        );

      case 'text':
        return <SafeText key={key} text={element.content as string} />;

      case 'image':
        if (element.imageId && element.imageType) {
          const altText = element.imageId.replace(/_/g, ' ').replace(/:/g, ' - ');

          if (element.imageType === 'icon') {
            const iconVariant =
              element.imageVariant && typeof element.imageVariant === 'string' && isIconVariant(element.imageVariant)
                ? element.imageVariant
                : 'small';

            return (
              <IconImage
                key={key}
                imageId={element.imageId}
                variant={iconVariant}
                alt={altText}
              />
            );
          }

          if (element.imageType === 'portrait') {
            const portraitVariant =
              element.imageVariant && typeof element.imageVariant === 'string' && isPortraitVariant(element.imageVariant)
                ? element.imageVariant
                : DEFAULT_PORTRAIT_VARIANT;

            return (
              <PortraitImage
                key={key}
                portraitId={element.imageId}
                variant={portraitVariant}
                alt={altText}
              />
            );
          }

          // Special handling for title-image: check if it's from an adventure tag
          // Pattern: adventureId:adventureId_with_underscores (e.g., portrait-gallery:portrait_gallery)
          if (element.imageType === 'title-image' && element.imageId.includes(':')) {
            const parts = element.imageId.split(':');
            const adventureId = parts[0];
            const imageIdPart = parts[1];
            if (adventureId && imageIdPart) {
              // Check if this looks like it came from an adventure tag (imageIdPart matches adventureId with underscores)
              const adventureIdWithUnderscores = adventureId.replace(/-/g, '_');
              if (imageIdPart === adventureIdWithUnderscores) {
                // This is from an <adventure> tag - fetch the real title image ID
                return (
                  <AdventureTitleImageFromId
                    key={key}
                    adventureId={adventureId}
                    variant="card"
                    alt={`Adventure: ${adventureId}`}
                    className="rich-text-image rich-text-adventure"
                  />
                );
              }
            }
          }

          // Fallback for non-icon images keeps existing behavior
          const fallbackVariant =
            element.imageVariant && typeof element.imageVariant === 'string'
              ? element.imageVariant
              : 'card';
          const imageUrl = generateImageUrl(element.imageId, element.imageType, fallbackVariant);
          let imageClass = 'rich-text-image';
          if (element.imageType === 'banner') {
            imageClass += ' rich-text-banner';
          }
          if (element.imageType === 'location-image') {
            imageClass += ' rich-text-location-image';
          }
          return (
            <img
              key={key}
              src={imageUrl}
              alt={altText}
              className={imageClass}
            />
          );
        }
        return null;

      case 'inline-option':
        if (!onInlineOptionClick) {
          console.warn('RichTextRenderer: onInlineOptionClick handler not provided for inline option');
          return null;
        }
        return (
          <InlineOption
            key={key}
            element={element}
            onOptionClick={onInlineOptionClick}
            disabled={disableInlineOptions}
            {...(customColor ? { customColor } : {})}
          />
        );

      case 'inventory-list':
        // Handle both old format (inventoryType/inventoryOwner) and new format (attributes)
        let characterName: string | undefined;
        let locationTag: string | undefined;
        
        if (element.inventoryType && element.inventoryOwner) {
          // Old format from parser
          if (element.inventoryType === 'character') {
            characterName = element.inventoryOwner;
          } else if (element.inventoryType === 'location') {
            locationTag = element.inventoryOwner;
          }
        } else {
          // New format with attributes
          const attributes = (element as any).attributes || {};
          characterName = attributes.character;
          locationTag = attributes.location;
        }
        
        if (!characterName && !locationTag) {
          console.warn('RichTextRenderer: inventory-list element missing character or location attribute');
          return null;
        }

        // Check if this is a selectable inventory list
        if (element.selectionTarget) {
          if (!onInventoryItemSelect) {
            console.warn('RichTextRenderer: onInventoryItemSelect handler not provided for selectable inventory list');
            return null;
          }
          
          return (
            <SelectableInventoryList
              key={key}
              {...(characterName ? { characterName } : {})}
              {...(locationTag ? { locationTag } : {})}
              selectionTarget={element.selectionTarget}
              onItemSelect={onInventoryItemSelect}
              className="rich-text-inventory-list"
            />
          );
        }
        
        // Regular (non-selectable) inventory list
        return (
          <InventoryList
            key={key}
            {...(characterName ? { characterName } : {})}
            {...(locationTag ? { locationTag } : {})}
            className="rich-text-inventory-list"
          />
        );

      case 'adventure-list':
        // Handle adventure-list tag: <Adventure-List>
        return (
          <AdventureList
            key={key}
            excludeSystemAdventures={true}
            className="rich-text-adventure-list"
            {...(customColor ? { customColor } : {})}
          />
        );

      case 'active-adventure':
        // Handle active-adventure tag: <active-adventure:session-id>
        if (!element.sessionId) {
          console.warn('RichTextRenderer: active-adventure element missing sessionId');
          return null;
        }
        return (
          <ActiveAdventureTitleImage
            key={key}
            sessionId={element.sessionId}
            variant="card"
            alt={`Active Adventure: ${element.sessionId}`}
            className="rich-text-image rich-text-active-adventure"
          />
        );

      case 'active-adventure-list':
        // Handle active-adventure-list tag: <Active-Adventure-List>
        return (
          <ActiveAdventureList
            key={key}
            excludeSystemAdventures={true}
            className="rich-text-active-adventure-list"
            {...(customColor ? { customColor } : {})}
          />
        );

      case 'friend-requests':
        // Handle friend-requests tag: <Friend-Requests selectionTarget=scene_id>
        if (element.selectionTarget) {
          if (!onFriendRequestSelect) {
            console.warn('RichTextRenderer: onFriendRequestSelect handler not provided for selectable friend requests');
            return null;
          }
          
          return (
            <FriendRequestList
              key={key}
              selectionTarget={element.selectionTarget}
              onRequestSelect={onFriendRequestSelect}
              className="rich-text-friend-requests"
            />
          );
        }
        
        // Regular (non-selectable) friend requests list
        return (
          <FriendRequestList
            key={key}
            className="rich-text-friend-requests"
          />
        );

      case 'friends-list':
        // Handle friends-list tag: <Friends-List selectionTarget=scene_id>
        if (element.selectionTarget) {
          if (!onFriendSelect) {
            console.warn('RichTextRenderer: onFriendSelect handler not provided for selectable friends list');
            return null;
          }
          
          return (
            <FriendsList
              key={key}
              selectionTarget={element.selectionTarget}
              onFriendSelect={onFriendSelect}
              className="rich-text-friends-list"
            />
          );
        }
        
        // Regular (non-selectable) friends list
        return (
          <FriendsList
            key={key}
            className="rich-text-friends-list"
          />
        );

      case 'adventure-invite-list':
        // Handle adventure-invite-list tag: <Adventure-Invite-List>
        // All adventure invites are clickable and will join the adventure
        return (
          <AdventureInviteList
            key={key}
            {...(onAdventureInviteClick && { onInviteClick: onAdventureInviteClick })}
            className="rich-text-adventure-invite-list"
          />
        );

      case 'adventure-invite-status-list':
        // Handle adventure-invite-status-list tag: <Adventure-Invite-Status-List selectionTarget=scene_id>
        if (element.selectionTarget) {
          if (!onAdventureInviteStatusSelect) {
            console.warn('RichTextRenderer: onAdventureInviteStatusSelect handler not provided for selectable adventure invite status list');
            return null;
          }
          
          return (
            <AdventureInviteStatusList
              key={key}
              onInviteClick={(inviteId) => onAdventureInviteStatusSelect(inviteId, element.selectionTarget!)}
              className="rich-text-adventure-invite-status-list"
            />
          );
        }
        
        // Regular (non-selectable) adventure invite status list
        return (
          <AdventureInviteStatusList
            key={key}
            className="rich-text-adventure-invite-status-list"
          />
        );

      case 'user-input':
        if (!element.userInputAttributes?.variable) {
          console.warn('RichTextRenderer: user-input element missing variable attribute');
          return null;
        }
        return (
          <UserInputField
            key={key}
            variableName={element.userInputAttributes.variable}
            {...(element.userInputAttributes.placeholder && { placeholder: element.userInputAttributes.placeholder })}
            {...(element.userInputAttributes.maxLength !== undefined && { maxLength: element.userInputAttributes.maxLength })}
            {...(element.userInputAttributes.autoSave !== undefined && { autoSave: element.userInputAttributes.autoSave })}
            {...(element.userInputAttributes.debounce !== undefined && { debounce: element.userInputAttributes.debounce })}
            {...(element.userInputAttributes.confirmationTarget && { confirmationTarget: element.userInputAttributes.confirmationTarget })}
            {...(element.userInputAttributes.type && { type: element.userInputAttributes.type })}
            className="rich-text-user-input-element"
          />
        );

      case 'divider':
        return (
          <hr
            key={key}
            className="rich-text-divider"
            aria-hidden="true"
          />
        );

      case 'conditional': {
        const branch: RichTextElement[] =
          Array.isArray(element.children) && element.children.length > 0
            ? element.children
            : Array.isArray(element.elseChildren)
              ? element.elseChildren
              : [];

        if (branch.length === 0) {
          return null;
        }

        return (
          <React.Fragment key={key}>
            {branch.map((child, childIndex) => renderElement(child, childIndex))}
          </React.Fragment>
        );
      }

      default:
        // Fallback for unknown element types
        const fallbackContent = Array.isArray(element.content)
          ? element.content.map((child, childIndex) => renderElement(child, childIndex))
          : <SafeText key={key} text={element.content as string} />;
        return fallbackContent;
    }
  };

  // Convert description to RichTextDescription if it's a string
  const richTextDescription = isRichTextDescription(description) 
    ? description 
    : plainTextToRichText(description);

  if (!richTextDescription.elements || richTextDescription.elements.length === 0) {
    return null;
  }

  return (
    <div className={`rich-text ${className}`} style={style}>
      {richTextDescription.elements.map((element, index) => renderElement(element, index))}
    </div>
  );
};

/**
 * Escapes HTML special characters to prevent XSS
 * @param text - Text to escape
 * @returns Escaped HTML string
 */
const escapeHtml = (text: string): string => {
  if (typeof text !== 'string') {
    return '';
  }
  
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
    // Note: Removed apostrophe escaping as it causes display issues in React components
};

/**
 * Returns true if the trimmed string looks like a single URL (http/https).
 */
const isStandaloneUrl = (text: string): boolean => {
  const t = typeof text === 'string' ? text.trim() : '';
  return t.length > 10 && (t.startsWith('http://') || t.startsWith('https://')) && !/\s/.test(t);
};

/**
 * Renders a long URL in a readable block with wrapping and a copy button.
 */
const CopyableLinkBlock: React.FC<{ url: string }> = ({ url }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      const t = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(t);
    } catch {
      // fallback for older browsers
      setCopied(false);
      return undefined;
    }
  }, [url]);

  return (
    <span className="rich-text-copyable-link-block" style={{
      display: 'block',
      marginTop: '0.5rem',
      marginBottom: '0.5rem',
      padding: '0.6rem 0.75rem',
      backgroundColor: 'rgba(0,0,0,0.2)',
      borderRadius: '6px',
      fontFamily: 'ui-monospace, monospace',
      fontSize: '0.85rem',
      wordBreak: 'break-all',
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
    }}>
      <span style={{ userSelect: 'text', cursor: 'text' }}>{url}</span>
      <button
        type="button"
        onClick={handleCopy}
        style={{
          display: 'block',
          marginTop: '0.5rem',
          padding: '0.35rem 0.75rem',
          fontSize: '0.875rem',
          borderRadius: '4px',
          border: '1px solid rgba(255,255,255,0.3)',
          background: 'rgba(255,255,255,0.15)',
          color: 'inherit',
          cursor: 'pointer',
        }}
      >
        {copied ? 'Copied!' : 'Copy link'}
      </button>
    </span>
  );
};

/**
 * Renders escaped text so HTML entities (e.g. &amp;) display correctly (e.g. &).
 * Uses dangerouslySetInnerHTML so the browser decodes entities; content is already escaped for safety.
 * If the text is a standalone URL, renders a copyable link block instead for better readability.
 */
const SafeText = ({ text }: { text: string }) => {
  if (isStandaloneUrl(text)) {
    return <CopyableLinkBlock url={text.trim()} />;
  }
  return <span dangerouslySetInnerHTML={{ __html: escapeHtml(text) }} />;
};

export default RichTextRenderer;
